import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import { 
  Download, Heart, Star, Search, FileText, 
  BookOpen, MessageSquare, Calendar as CalendarIcon,
  CheckSquare, Users, Briefcase, GraduationCap, Eye, AlertCircle, Plus, Bell, Trash2,
  Upload, File, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'All Resources', icon: FileText },
  { value: 'communication_cards', label: 'Communication Cards', icon: MessageSquare },
  { value: 'blog', label: 'Blogs', icon: BookOpen },
  { value: 'visual_schedule', label: 'Visual Schedules', icon: CalendarIcon },
  { value: 'social_story', label: 'Social Stories', icon: BookOpen },
  { value: 'emotion_chart', label: 'Emotion Charts', icon: Heart },
  { value: 'communication', label: 'Communication & AAC', icon: MessageSquare },
  { value: 'sensory', label: 'Sensory Tools', icon: Star },
  { value: 'accommodation', label: 'Accommodations', icon: CheckSquare },
  { value: 'guide', label: 'Guides & Toolkits', icon: FileText },
  { value: 'worksheet', label: 'Worksheets & Planners', icon: FileText },
  { value: 'book', label: 'Books & eBooks', icon: BookOpen },
  { value: 'video', label: 'Videos & Talks', icon: FileText },
  { value: 'app', label: 'Apps & Tools', icon: FileText },
  { value: 'external_link', label: 'Autistic-Led Orgs', icon: Users }
];

const topics = [
  { value: 'all', label: 'All Topics' },
  { value: 'early_childhood', label: 'Early Childhood' },
  { value: 'school_age', label: 'School Age' },
  { value: 'teens', label: 'Teens' },
  { value: 'adults', label: 'Adults' },
  { value: 'employment', label: 'Employment' },
  { value: 'relationships', label: 'Relationships' },
  { value: 'mental_health', label: 'Mental Health' }
];

export default function Resources() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasNewResources, setHasNewResources] = useState(false);
  const [lastChecked, setLastChecked] = useState(() => {
    return localStorage.getItem('resources_last_checked') || new Date().toISOString();
  });
  
  // File upload state
  const [uploadMode, setUploadMode] = useState('content'); // 'content' or 'file'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    category: '',
    topic: '',
    content: '',
    download_url: '',
    external_url: ''
  });

  useEffect(() => {
    fetchResources();
  }, [selectedCategory, selectedTopic]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedTopic !== 'all') params.append('topic', selectedTopic);
      
      const response = await axios.get(`${API}/resources?${params.toString()}`);
      setResources(response.data);
      
      // Check for new resources
      const newResourcesExist = response.data.some(r => 
        new Date(r.created_at) > new Date(lastChecked)
      );
      setHasNewResources(newResourcesExist);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = () => {
    const now = new Date().toISOString();
    localStorage.setItem('resources_last_checked', now);
    setLastChecked(now);
    setHasNewResources(false);
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    if (!newResource.title || !newResource.description || !newResource.category || !newResource.topic) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate based on upload mode
    if (uploadMode === 'file' && !selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    if (uploadMode === 'content' && !newResource.content && !newResource.external_url) {
      toast.error('Please enter content or an external URL');
      return;
    }
    
    setSubmitting(true);
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('token');
      
      if (uploadMode === 'file' && selectedFile) {
        // File upload
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('title', newResource.title);
        formData.append('description', newResource.description);
        formData.append('category', newResource.category);
        formData.append('topic', newResource.topic);
        
        await axios.post(`${API}/resources/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
        toast.success('File uploaded successfully!');
      } else {
        // Content-based resource
        await axios.post(`${API}/resources`, newResource, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Resource created successfully!');
      }
      
      // Reset form
      setNewResource({
        title: '', description: '', category: '', topic: '', content: '', download_url: '', external_url: ''
      });
      setSelectedFile(null);
      setUploadProgress(0);
      setCreateOpen(false);
      fetchResources();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create resource');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      setSelectedFile(file);
    }
  };
  
  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource deleted');
      fetchResources();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const handleFavorite = async (resourceId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/resources/${resourceId}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.action === 'added' ? 'Added to favorites!' : 'Removed from favorites');
      fetchResources();
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const filteredResources = resources.filter(r =>
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isFavorited = (resourceId) => {
    return user?.favorites?.includes(resourceId);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-12">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge>Tools & Downloads</Badge>
              {hasNewResources && (
                <Badge className="bg-primary text-white animate-pulse">
                  <Bell className="h-3 w-3 mr-1" />
                  New!
                </Badge>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Resource Library</h1>
            <p className="text-muted-foreground text-lg">
              Practical resources you can use immediately - visual schedules, social stories, 
              accommodation templates, and more.
            </p>
            {hasNewResources && (
              <Button variant="outline" size="sm" className="mt-4" onClick={markAsRead}>
                Mark all as read
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container-main py-8">
        {/* Autistic Voices CTA */}
        <Card className="mb-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h3 className="font-semibold text-lg mb-2">We Want to Hear From Autistic Voices</h3>
                <p className="text-muted-foreground">
                  If you are autistic and would like to contribute something to the resource hub, we would love to hear from you!
                </p>
              </div>
              <a href="mailto:Admin@autismacceptance.ky?subject=Resource Contribution from Autistic Voice">
                <Button className="btn-primary whitespace-nowrap">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Get in Touch
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Admin Create Button */}
        {isAdmin && (
          <div className="mb-6">
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Resource
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Resource</DialogTitle>
                  <DialogDescription>
                    Add a new resource to the library. Upload files (PDFs, documents) or create content directly.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateResource} className="space-y-4">
                  {/* Upload Mode Toggle */}
                  <div className="flex gap-2 p-1 bg-muted rounded-lg">
                    <button
                      type="button"
                      onClick={() => setUploadMode('content')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        uploadMode === 'content' 
                          ? 'bg-background shadow-sm text-foreground' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <FileText className="h-4 w-4 inline mr-2" />
                      Write Content
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadMode('file')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        uploadMode === 'file' 
                          ? 'bg-background shadow-sm text-foreground' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Upload className="h-4 w-4 inline mr-2" />
                      Upload File
                    </button>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={newResource.title}
                        onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                        required
                        data-testid="resource-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select
                        value={newResource.category}
                        onValueChange={(v) => setNewResource({ ...newResource, category: v })}
                      >
                        <SelectTrigger data-testid="resource-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.filter(c => c.value !== 'all').map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="topic">Topic *</Label>
                    <Select
                      value={newResource.topic}
                      onValueChange={(v) => setNewResource({ ...newResource, topic: v })}
                    >
                      <SelectTrigger data-testid="resource-topic-select">
                        <SelectValue placeholder="Select topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {topics.filter(t => t.value !== 'all').map((topic) => (
                          <SelectItem key={topic.value} value={topic.value}>{topic.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={newResource.description}
                      onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                      placeholder="Brief description of the resource..."
                      rows={2}
                      required
                      data-testid="resource-description-input"
                    />
                  </div>
                  
                  {/* File Upload Section */}
                  {uploadMode === 'file' ? (
                    <div className="space-y-3">
                      <Label>Upload File *</Label>
                      <div 
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          selectedFile ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                        }`}
                      >
                        {selectedFile ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <File className="h-10 w-10 text-primary" />
                              <div className="text-left">
                                <p className="font-medium">{selectedFile.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon"
                              onClick={removeSelectedFile}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                            <p className="text-sm text-muted-foreground mb-2">
                              Drag and drop your file here, or click to browse
                            </p>
                            <p className="text-xs text-muted-foreground">
                              PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG, GIF (Max 10MB)
                            </p>
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif"
                          className={selectedFile ? 'hidden' : 'absolute inset-0 w-full h-full opacity-0 cursor-pointer'}
                          style={selectedFile ? {} : { position: 'absolute', inset: 0 }}
                          data-testid="file-upload-input"
                        />
                      </div>
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="content">Content (Markdown supported)</Label>
                        <Textarea
                          id="content"
                          value={newResource.content}
                          onChange={(e) => setNewResource({ ...newResource, content: e.target.value })}
                          placeholder="Full content of the resource. Use Markdown for formatting..."
                          rows={10}
                          data-testid="resource-content-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Supports Markdown: # Headings, **bold**, *italic*, - lists, | tables |
                        </p>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="download_url">Download URL (optional)</Label>
                          <Input
                            id="download_url"
                            type="url"
                            value={newResource.download_url}
                            onChange={(e) => setNewResource({ ...newResource, download_url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="external_url">External Link (optional)</Label>
                          <Input
                            id="external_url"
                            type="url"
                            value={newResource.external_url}
                            onChange={(e) => setNewResource({ ...newResource, external_url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <DialogFooter>
                    <Button type="submit" disabled={submitting} className="btn-primary" data-testid="create-resource-btn">
                      {submitting ? (
                        uploadMode === 'file' ? `Uploading... ${uploadProgress}%` : 'Creating...'
                      ) : (
                        uploadMode === 'file' ? 'Upload Resource' : 'Create Resource'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-md"
            data-testid="resource-search"
          />
        </div>

        {/* Topics Tabs */}
        <Tabs value={selectedTopic} onValueChange={setSelectedTopic} className="mb-6">
          <TabsList className="flex flex-wrap h-auto gap-1">
            {topics.map((topic) => (
              <TabsTrigger 
                key={topic.value} 
                value={topic.value}
                className="text-sm"
              >
                {topic.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.value)}
              className="gap-2"
            >
              <cat.icon className="h-4 w-4" />
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Resources Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-muted rounded w-2/3 mb-4"></div>
                  <div className="h-4 bg-muted rounded w-full mb-2"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredResources.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Resources Found</h3>
              <p className="text-muted-foreground">
                Try adjusting your filters or search term.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => {
              const isNew = new Date(resource.created_at) > new Date(lastChecked);
              return (
                <Card 
                  key={resource.id} 
                  className={`card-base h-full flex flex-col ${isNew ? 'ring-2 ring-primary/30' : ''}`} 
                  data-testid={`resource-${resource.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="mb-2">
                          {categories.find(c => c.value === resource.category)?.label || resource.category}
                        </Badge>
                        {isNew && <Badge className="bg-primary text-white text-xs">New</Badge>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleFavorite(resource.id)}
                          className={isFavorited(resource.id) ? 'text-pink-500' : 'text-muted-foreground'}
                          data-testid={`favorite-${resource.id}`}
                        >
                          <Heart className={`h-5 w-5 ${isFavorited(resource.id) ? 'fill-current' : ''}`} />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteResource(resource.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-lg">{resource.title}</CardTitle>
                    <CardDescription>{resource.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-end">
                    <div className="flex items-center justify-between mt-4">
                      <Badge variant="secondary">
                        {topics.find(t => t.value === resource.topic)?.label || resource.topic}
                      </Badge>
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Heart className="h-3 w-3" />
                        {resource.favorites_count}
                      </span>
                    </div>
                    
                    {/* View Resource - Opens printable version directly */}
                    {resource.content && (
                      <Button 
                        variant="outline" 
                        className="w-full mt-4" 
                        data-testid={`view-resource-${resource.id}`}
                        onClick={() => {
                          // Open in new tab as printable page with logo
                          const printWindow = window.open('', '_blank');
                          printWindow.document.write(`
                            <!DOCTYPE html>
                            <html>
                            <head>
                              <title>${resource.title} - Autism Acceptance Resources Hub</title>
                              <style>
                                body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.8; color: #333; }
                                h1 { color: #e52b5b; border-bottom: 2px solid #e52b5b; padding-bottom: 10px; margin-top: 20px; }
                                h2 { color: #ff8c00; margin-top: 30px; }
                                h3 { color: #333; margin-top: 20px; }
                                p { margin: 15px 0; }
                                table { border-collapse: collapse; width: 100%; margin: 20px 0; }
                                th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                                th { background-color: #f5f5f5; }
                                ul, ol { margin: 15px 0; padding-left: 30px; }
                                li { margin: 10px 0; }
                                hr { border: none; border-top: 1px solid #ddd; margin: 30px 0; }
                                .disclaimer { background: #fff3cd; padding: 15px; border-radius: 8px; margin-top: 30px; font-size: 14px; }
                                .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #e52b5b; }
                                .header img { max-width: 200px; height: auto; margin-bottom: 10px; }
                                .header-text { font-size: 14px; color: #666; margin-top: 10px; }
                                .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; }
                                .footer img { max-width: 120px; height: auto; opacity: 0.7; }
                                @media print { body { margin: 20px; } .no-print { display: none; } }
                              </style>
                            </head>
                            <body>
                              <div class="header">
                                <img src="${window.location.origin}/logo.png" alt="Autism Acceptance" />
                                <p class="header-text">Resources Hub - Cayman Islands</p>
                              </div>
                              <h1>${resource.title}</h1>
                              <p><em>${resource.description}</em></p>
                              <hr/>
                              ${resource.content.replace(/^# .+$/gm, '').replace(/^## (.+)$/gm, '<h2>$1</h2>').replace(/^### (.+)$/gm, '<h3>$1</h3>').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n- (.+)/g, '<li>$1</li>').replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>')}
                              <div class="disclaimer">
                                <strong>Disclaimer:</strong> This resource is provided for educational and informational purposes only. It does not represent the official views or opinions of Autism Acceptance. Always consult with qualified professionals for specific guidance.
                              </div>
                              <div class="footer">
                                <img src="${window.location.origin}/logo.png" alt="Autism Acceptance" />
                                <p style="font-size: 12px; color: #999;">© Autism Acceptance Cayman Islands | NP-693</p>
                              </div>
                              <p class="no-print" style="margin-top: 30px; text-align: center;">
                                <button onclick="window.print()" style="padding: 10px 30px; background: #e52b5b; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px;">Print / Save as PDF</button>
                              </p>
                            </body>
                            </html>
                          `);
                          printWindow.document.close();
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    )}
                    
                    {/* External URL - Opens in new tab */}
                    {resource.external_url && !resource.content && (
                      <a href={resource.external_url} target="_blank" rel="noopener noreferrer" className="mt-4 block">
                        <Button variant="default" className="w-full btn-primary">
                          <Eye className="h-4 w-4 mr-2" />
                          Visit Resource
                        </Button>
                      </a>
                    )}
                    
                    {/* View/Download buttons for uploaded files */}
                    {resource.download_url && (
                      <div className="mt-4 flex gap-2">
                        {/* View button - opens in new tab */}
                        <a 
                          href={`${resource.download_url.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${resource.download_url}` 
                            : resource.download_url
                          }?view=true`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex-1"
                          data-testid={`view-resource-${resource.id}`}
                        >
                          <Button variant="outline" className="w-full">
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </a>
                        {/* Download button */}
                        <a 
                          href={resource.download_url.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${resource.download_url}` 
                            : resource.download_url
                          } 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex-1"
                          data-testid={`download-resource-${resource.id}`}
                        >
                          <Button variant="outline" className="w-full">
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
